import { pickRealStore } from "@/lib/merchantStore";

type Row = { id: string; created_at?: string | null };
type StoresRow = Row & { plan?: string | null; billing_status?: string | null };
type SubRow = { store_id: string | null; status: string | null; end_date: string | null };

/**
 * The ONLY store id that dashboard access may be decided on: the merchant's real store
 * (pickRealStore: store_settings first, the auto-created "my-store" stores row only when
 * there is no store_settings at all). `storesRow` is the stores row carrying that same id,
 * if any — never a different (e.g. "my-store") row.
 */
export function pickAccessStore(
  settingsRows: Row[] | null | undefined,
  storesRows: StoresRow[] | null | undefined,
): { storeId: string; storesRow: StoresRow | null } | null {
  const picked = pickRealStore(settingsRows, storesRows);
  if (!picked) return null;
  const storeId = picked.row.id;
  const storesRow = (storesRows ?? []).find((r) => r.id === storeId) ?? null;
  return { storeId, storesRow };
}

/** Paid via the stores row of the real store: plan != 'free' OR billing_status == 'active'. */
export function storesRowGrantsPaid(row: StoresRow | null | undefined): boolean {
  if (!row) return false;
  const plan = (row.plan ?? "free").trim().toLowerCase();
  const billing = (row.billing_status ?? "").trim().toLowerCase();
  return plan !== "free" || billing === "active";
}

/** Only subscriptions attached to the real store id count; "my-store" subscriptions never do. */
export function subscriptionsForStore<T extends SubRow>(rows: T[] | null | undefined, storeId: string): T[] {
  return (rows ?? []).filter((r) => r.store_id === storeId);
}
